import { FunctionComponent, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { LoadingOverlay, OutputLanguageSelect } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { StepInfo } from "@/components/StepInfo/StepInfo";
import { TextArea } from "@/components/TextArea/TextArea";

import {
	useGetCompanySignalConfigQuery,
	useUpdateCompanySignalConfigMutation,
	useTestSignalConfigMutation,
} from "@/pages/Private/redux/signal/signal.api";

import {
	CompanySignalConfig as CompanySignalConfigType,
	SignalPriorityItem,
	SignalPriorityLevel,
	CreateSignalPriorityItem,
	TestSignalConfigResponse,
} from "../schema/signal";
import { PrioritySection } from "./PrioritySection";
import { PriorityDefinitionBoard } from "./PriorityDefinitions/PriorityDefinitionBoard";
import { PriorityItemModal } from "./PriorityItemModal";
import { TestSignalConfigModal } from "./TestSignalConfigModal";

// Default signal config for new company config
const getDefaultSignalConfig = (companyId: number): CompanySignalConfigType => ({
	companyId,
	deepResearchPrompt: "",
	leadUpdatesPrompt: "",
	companyUpdatesPrompt: "",
	industryUpdatesPrompt: "",
	systemPrompt: "",
	checkLinkedinPosts: true,
	analyzeLinkedinEngagement: true,
	checkTechStackChanges: true,
	language: null,
	priorities: [],
});

export const SignalConfig: FunctionComponent = () => {
	const { t } = useTranslation();

	// API hooks
	const { data: signalConfigData, isFetching: isSignalConfigFetching } =
		useGetCompanySignalConfigQuery();
	const [updateSignalConfig, { isLoading: isSaving }] = useUpdateCompanySignalConfigMutation();
	const [testSignalConfig, { isLoading: isTesting }] = useTestSignalConfigMutation();

	// Local state for signal configuration
	const [signalConfig, setSignalConfig] = useState<CompanySignalConfigType>(() =>
		getDefaultSignalConfig(0)
	);

	// Sync API data to local state
	useEffect(() => {
		if (signalConfigData) {
			setSignalConfig(signalConfigData);
		}
	}, [signalConfigData]);

	// Test modal state
	const [testModalOpen, setTestModalOpen] = useState(false);
	const [testResult, setTestResult] = useState<TestSignalConfigResponse | null>(null);
	const [testError, setTestError] = useState<string | null>(null);

	// Modal state
	const [modalOpen, setModalOpen] = useState(false);
	const [editingItem, setEditingItem] = useState<SignalPriorityItem | null>(null);
	const [editingPriorityLevel, setEditingPriorityLevel] = useState<SignalPriorityLevel>(
		SignalPriorityLevel.PRIORITY_1
	);
	const [modalKey, setModalKey] = useState(0);

	// Priority item handlers
	const handleAddPriorityItem = (priorityLevel: SignalPriorityLevel) => {
		setEditingPriorityLevel(priorityLevel);
		setEditingItem(null);
		setModalKey((prev) => prev + 1);
		setModalOpen(true);
	};

	const handleEditPriorityItem = (item: SignalPriorityItem) => {
		setEditingPriorityLevel(item.priorityLevel);
		setEditingItem(item);
		setModalKey((prev) => prev + 1);
		setModalOpen(true);
	};

	const handleDeletePriorityItem = (item: SignalPriorityItem) => {
		setSignalConfig((prev) => ({
			...prev,
			priorities: prev.priorities.filter(
				(p) => !(p.title === item.title && p.priorityLevel === item.priorityLevel)
			),
		}));
	};

	const handleSavePriorityItem = (item: CreateSignalPriorityItem & { id?: number }) => {
		setSignalConfig((prev) => {
			const existingIndex = prev.priorities.findIndex(
				(p) =>
					p.id === item.id ||
					(p.title === editingItem?.title && p.priorityLevel === editingItem?.priorityLevel)
			);

			if (existingIndex >= 0) {
				const updated = [...prev.priorities];

				updated[existingIndex] = { ...updated[existingIndex], ...item };

				return { ...prev, priorities: updated };
			} else {
				return {
					...prev,
					priorities: [...prev.priorities, { ...item, id: Date.now() }],
				};
			}
		});
	};

	// PREC-3908: a card dragged into another priority box. The board hands back the whole
	// reordered array, so this only has to store it — and only fires for a real move.
	const handleMovePriorityItem = (priorities: SignalPriorityItem[]) => {
		setSignalConfig((prev) => ({ ...prev, priorities }));
	};

	const getPriorityItems = (level: SignalPriorityLevel) =>
		signalConfig.priorities.filter((p) => p.priorityLevel === level);

	const handleTest = async () => {
		setTestResult(null);
		setTestError(null);
		setTestModalOpen(true);

		try {
			const prioritiesWithOrder = signalConfig.priorities.map((p, idx) => ({
				...(p.id ? { id: p.id } : {}),
				priorityLevel: p.priorityLevel,
				title: p.title,
				definition: p.definition,
				examples: p.examples,
				order: p.order ?? idx,
			}));

			const result = await testSignalConfig({
				signalConfig: {
					deepResearchPrompt: signalConfig.deepResearchPrompt,
					systemPrompt: signalConfig.systemPrompt,
					leadUpdatesPrompt: signalConfig.leadUpdatesPrompt,
					companyUpdatesPrompt: signalConfig.companyUpdatesPrompt,
					industryUpdatesPrompt: signalConfig.industryUpdatesPrompt,
					language: signalConfig.language,
					priorities: prioritiesWithOrder,
				},
			}).unwrap();

			setTestResult(result);
		} catch (err) {
			setTestError(err instanceof Error ? err.message : String(err));
		}
	};

	const handleSave = async () => {
		try {
			const prioritiesWithOrder = signalConfig.priorities.map((p, idx) => ({
				...(p.id ? { id: p.id } : {}),
				priorityLevel: p.priorityLevel,
				title: p.title,
				definition: p.definition,
				examples: p.examples,
				order: p.order ?? idx,
			}));

			await updateSignalConfig({
				deepResearchPrompt: signalConfig.deepResearchPrompt,
				systemPrompt: signalConfig.systemPrompt,
				leadUpdatesPrompt: signalConfig.leadUpdatesPrompt,
				companyUpdatesPrompt: signalConfig.companyUpdatesPrompt,
				industryUpdatesPrompt: signalConfig.industryUpdatesPrompt,
				checkLinkedinPosts: signalConfig.checkLinkedinPosts,
				analyzeLinkedinEngagement: signalConfig.analyzeLinkedinEngagement,
				checkTechStackChanges: signalConfig.checkTechStackChanges,
				language: signalConfig.language,
				priorities: prioritiesWithOrder,
			}).unwrap();
		} catch (error) {
			console.error("Error saving signal config:", error);
		}
	};

	if (isSignalConfigFetching) {
		return <LoadingOverlay />;
	}

	return (
		<div className="w-full">
			<StepInfo
				description={
					<p>
						Configure default signal definitions and priorities for your company. These settings
						will serve as templates when creating new TRACKER lists.
					</p>
				}
				title={t("list.signals.title")}
			/>

			{/* AI Prompts Section */}
			<div className="p-4 mt-8 rounded-xl border border-gray-200 bg-brand-white">
				<h3 className="text-sm font-semibold mb-4">{t("list.signals.aiPrompts")}</h3>
				<div className="space-y-4">
					<TextArea
						handleChange={(e) =>
							setSignalConfig((prev) => ({ ...prev, deepResearchPrompt: e.target.value }))
						}
						label={t("list.signals.deepResearchPrompt")}
						name="deepResearchPrompt"
						placeholder={t("list.signals.deepResearchPromptPlaceholder")}
						showError={false}
						value={signalConfig.deepResearchPrompt}
					/>
					<div className="max-w-[320px]">
						<OutputLanguageSelect
							handleSelect={(language) => setSignalConfig((prev) => ({ ...prev, language }))}
							label={t("list.signals.outputLanguage")}
							value={signalConfig.language}
						/>
					</div>
				</div>
			</div>

			{/* Signal Categorization Section */}
			<div className="p-4 mt-6 rounded-xl border border-gray-200 bg-brand-white">
				<h3 className="text-sm font-semibold mb-4">{t("list.signals.signalCategorization")}</h3>
				<div className="space-y-4">
					<TextArea
						handleChange={(e) =>
							setSignalConfig((prev) => ({ ...prev, leadUpdatesPrompt: e.target.value }))
						}
						label={t("list.signals.leadUpdates")}
						labelClassName="font-semibold"
						name="leadUpdatesPrompt"
						placeholder={t("list.signals.leadUpdatesPlaceholder")}
						showError={false}
						value={signalConfig.leadUpdatesPrompt}
					/>
					<TextArea
						handleChange={(e) =>
							setSignalConfig((prev) => ({ ...prev, companyUpdatesPrompt: e.target.value }))
						}
						label={t("list.signals.companyUpdates")}
						labelClassName="font-semibold"
						name="companyUpdatesPrompt"
						placeholder={t("list.signals.companyUpdatesPlaceholder")}
						showError={false}
						value={signalConfig.companyUpdatesPrompt}
					/>
					<TextArea
						handleChange={(e) =>
							setSignalConfig((prev) => ({
								...prev,
								industryUpdatesPrompt: e.target.value,
							}))
						}
						label={t("list.signals.industryUpdates")}
						labelClassName="font-semibold"
						name="industryUpdatesPrompt"
						placeholder={t("list.signals.industryUpdatesPlaceholder")}
						showError={false}
						value={signalConfig.industryUpdatesPrompt}
					/>
				</div>
			</div>

			{/* Priority Definitions Section */}
			<div className="p-4 mt-6 rounded-xl border border-border-light bg-brand-white">
				<h3 className="text-sm font-semibold mb-4">{t("list.signals.priorityDefinitions")}</h3>
				<p className="text-sm text-text-meta mb-6">
					{t("list.signals.priorityDefinitionsDescription")}
				</p>
				<PriorityDefinitionBoard
					className="grid grid-cols-1 lg:grid-cols-2 gap-6"
					items={signalConfig.priorities}
					onItemsChange={handleMovePriorityItem}
				>
					<PrioritySection
						movable
						items={getPriorityItems(SignalPriorityLevel.PRIORITY_1)}
						priorityLevel={SignalPriorityLevel.PRIORITY_1}
						onAddItem={handleAddPriorityItem}
						onDeleteItem={handleDeletePriorityItem}
						onEditItem={handleEditPriorityItem}
					/>
					<PrioritySection
						movable
						items={getPriorityItems(SignalPriorityLevel.PRIORITY_2)}
						priorityLevel={SignalPriorityLevel.PRIORITY_2}
						onAddItem={handleAddPriorityItem}
						onDeleteItem={handleDeletePriorityItem}
						onEditItem={handleEditPriorityItem}
					/>
					<PrioritySection
						movable
						items={getPriorityItems(SignalPriorityLevel.PRIORITY_3)}
						priorityLevel={SignalPriorityLevel.PRIORITY_3}
						onAddItem={handleAddPriorityItem}
						onDeleteItem={handleDeletePriorityItem}
						onEditItem={handleEditPriorityItem}
					/>
					<PrioritySection
						movable
						items={getPriorityItems(SignalPriorityLevel.DISQUALIFIED)}
						priorityLevel={SignalPriorityLevel.DISQUALIFIED}
						onAddItem={handleAddPriorityItem}
						onDeleteItem={handleDeletePriorityItem}
						onEditItem={handleEditPriorityItem}
					/>
				</PriorityDefinitionBoard>
			</div>

			{/* Floating Action Buttons */}
			<div className="fixed bottom-8 right-8 z-50 flex gap-3">
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					disabled={isTesting}
					isLoading={isTesting}
					title={t("list.signals.testConfiguration")}
					onClick={handleTest}
				/>
				<Button
					disabled={isSaving}
					isLoading={isSaving}
					title={t("basics.save")}
					onClick={handleSave}
				/>
			</div>

			{/* Priority Item Modal */}
			<PriorityItemModal
				key={modalKey}
				isOpen={modalOpen}
				item={editingItem}
				priorityLevel={editingPriorityLevel}
				onClose={() => setModalOpen(false)}
				onSave={handleSavePriorityItem}
			/>

			{/* Test Signal Config Modal */}
			<TestSignalConfigModal
				error={testError}
				isLoading={isTesting}
				isOpen={testModalOpen}
				result={testResult}
				onClose={() => setTestModalOpen(false)}
			/>
		</div>
	);
};
